import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  FileText, Send, CheckCircle, Users, Calendar, Download, 
  PlayCircle, UserPlus, ClipboardList, Award, Clock, Star, AlertCircle, TrendingUp, DollarSign
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function UnifiedJoiningFlow({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pipeline');
  
  // Pipeline states
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [preTasks, setPreTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Probation tracking states
  const [probationEmployees, setProbationEmployees] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedProbationEmployee, setSelectedProbationEmployee] = useState(null);
  const [reviewData, setReviewData] = useState({
    employee_id: '',
    review_date: new Date().toISOString().split('T')[0],
    performance_rating: 3,
    attendance_rating: 3,
    behavior_rating: 3,
    manager_feedback: '',
    recommendation: 'Confirm'
  });
  
  // Confirmation states
  const [confirmationEmployees, setConfirmationEmployees] = useState([]);
  const [showConfirmationForm, setShowConfirmationForm] = useState(false);
  const [selectedConfirmationEmployee, setSelectedConfirmationEmployee] = useState(null);
  const [confirmationData, setConfirmationData] = useState({
    employee_id: '',
    confirmation_date: new Date().toISOString().split('T')[0],
    new_designation: '',
    salary_revision: 0,
    additional_benefits: []
  });

  // Stats
  const [stats, setStats] = useState({
    selected: 0,
    offer_sent: 0,
    offer_accepted: 0,
    documents_generated: 0,
    pre_joining_complete: 0,
    employees_added: 0
  });

  useEffect(() => {
    if (activeTab === 'pipeline') {
      fetchCandidatesInPipeline();
      fetchStats();
    } else if (activeTab === 'probation') {
      fetchProbationEmployees();
    } else if (activeTab === 'confirmation') {
      fetchConfirmationEmployees();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedCandidate) {
      fetchCandidateDetails(selectedCandidate.id);
    }
  }, [selectedCandidate]);

  const fetchCandidatesInPipeline = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/recruitment/candidates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { stage: 'Selected,Offer Sent,Offer Accepted' }
      });
      
      const allCandidates = response.data.candidates || [];
      setCandidates(allCandidates);
      
      if (allCandidates.length > 0 && !selectedCandidate) {
        setSelectedCandidate(allCandidates[0]);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/recruitment/candidates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const all = response.data.candidates || [];
      setStats({
        selected: all.filter(c => c.stage === 'Selected').length,
        offer_sent: all.filter(c => c.stage === 'Offer Sent').length,
        offer_accepted: all.filter(c => c.stage === 'Offer Accepted').length,
        documents_generated: documents.length,
        pre_joining_complete: preTasks.filter(t => t.status === 'Completed').length,
        employees_added: 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCandidateDetails = async (candidateId) => {
    try {
      const token = localStorage.getItem('token');
      
      const tasksRes = await axios.get(`${API}/recruitment/pre-joining/tasks/${candidateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreTasks(tasksRes.data.tasks || []);
      
    } catch (error) {
      console.error('Error fetching candidate details:', error);
    }
  };

  const fetchProbationEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/recruitment/probation/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProbationEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching probation employees:', error);
    }
  };

  const fetchConfirmationEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/recruitment/probation/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const eligible = (response.data.employees || []).filter(
        emp => emp.probation_days_remaining <= 30 || emp.probation_status === 'Confirm'
      );
      setConfirmationEmployees(eligible);
    } catch (error) {
      console.error('Error fetching confirmation employees:', error);
    }
  };

  const generateOfferLetter = async () => {
    if (!selectedCandidate) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        candidate_name: `${selectedCandidate.first_name} ${selectedCandidate.last_name}`,
        designation: selectedCandidate.designation || 'Employee',
        location: selectedCandidate.current_location || 'Head Office',
        joining_date: selectedCandidate.expected_joining_date || new Date().toISOString().split('T')[0],
        ctc: selectedCandidate.expected_ctc || 0
      };

      await axios.post(`${API}/documents/generate-offer-letter`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Offer Letter Generated!');
      
      await axios.patch(`${API}/recruitment/candidates/${selectedCandidate.id}`, {
        stage: 'Offer Sent',
        offer_sent_date: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchCandidatesInPipeline();
      fetchStats();
    } catch (error) {
      alert('❌ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const generateAppointmentLetter = async () => {
    if (!selectedCandidate) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        employee_id: selectedCandidate.id,
        employee_name: `${selectedCandidate.first_name} ${selectedCandidate.last_name}`,
        employee_code: `ZBX-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
        designation: selectedCandidate.designation || 'Employee',
        reporting_to: 'HR Manager',
        location: selectedCandidate.current_location || 'Head Office',
        notice_period_days: 30
      };

      await axios.post(`${API}/documents/generate-appointment-letter`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Appointment Letter Generated!');
      fetchCandidatesInPipeline();
    } catch (error) {
      alert('❌ Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const markOfferAccepted = async () => {
    if (!selectedCandidate) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/recruitment/candidates/${selectedCandidate.id}`, {
        stage: 'Offer Accepted',
        offer_accepted_date: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ Offer marked as Accepted!');
      fetchCandidatesInPipeline();
      fetchStats();
    } catch (error) {
      alert('❌ Error: ' + error.message);
    }
  };

  const convertToEmployee = async () => {
    if (!selectedCandidate) return;
    
    const confirmed = window.confirm(
      `Convert ${selectedCandidate.first_name} ${selectedCandidate.last_name} to Employee?\n\n` +
      `This will create an employee record and start onboarding.`
    );
    
    if (!confirmed) return;

    navigate('/onboarding/new', { 
      state: { 
        candidate: selectedCandidate,
        fromJoiningFlow: true 
      } 
    });
  };

  const handleProbationReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/recruitment/probation/reviews`, reviewData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Probation review submitted successfully!');
      setShowReviewForm(false);
      setSelectedProbationEmployee(null);
      resetReviewForm();
      fetchProbationEmployees();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleConfirmationSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/recruitment/confirmation`, confirmationData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Confirmation letter issued successfully!');
      setShowConfirmationForm(false);
      setSelectedConfirmationEmployee(null);
      resetConfirmationForm();
      fetchConfirmationEmployees();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetReviewForm = () => {
    setReviewData({
      employee_id: '',
      review_date: new Date().toISOString().split('T')[0],
      performance_rating: 3,
      attendance_rating: 3,
      behavior_rating: 3,
      manager_feedback: '',
      recommendation: 'Confirm'
    });
  };

  const resetConfirmationForm = () => {
    setConfirmationData({
      employee_id: '',
      confirmation_date: new Date().toISOString().split('T')[0],
      new_designation: '',
      salary_revision: 0,
      additional_benefits: []
    });
  };

  const getStageColor = (stage) => {
    const colors = {
      'Selected': 'bg-blue-100 text-blue-700',
      'Offer Sent': 'bg-yellow-100 text-yellow-700',
      'Offer Accepted': 'bg-green-100 text-green-700'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  };

  const getProbationStatusColor = (daysRemaining) => {
    if (daysRemaining <= 30) return '#f44336';
    if (daysRemaining <= 60) return '#ff9800';
    return '#4caf50';
  };

  const getProgress = () => {
    if (!selectedCandidate) return 0;
    
    let progress = 0;
    if (selectedCandidate.stage === 'Selected') progress = 20;
    if (selectedCandidate.stage === 'Offer Sent') progress = 40;
    if (selectedCandidate.stage === 'Offer Accepted') progress = 60;
    
    const completedTasks = preTasks.filter(t => t.status === 'Completed').length;
    const totalTasks = preTasks.length;
    if (totalTasks > 0) {
      progress += (completedTasks / totalTasks) * 40;
    }
    
    return Math.min(progress, 100);
  };

  const RatingStars = ({ value, onChange, label }) => (
    <div className="mb-3">
      <label className="block text-sm font-medium mb-2">{label}</label>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="focus:outline-none"
          >
            <Star
              className={`w-6 h-6 ${star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
            />
          </button>
        ))}
        <span className="ml-2 text-sm font-medium">{value}/5</span>
      </div>
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>
            Employee Lifecycle Management
          </h1>
          <p className="text-gray-600 mt-2">
            Complete journey: Recruitment → Offer → Joining → Probation → Confirmation
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('pipeline')}
            className={`px-6 py-3 font-medium border-b-2 transition ${
              activeTab === 'pipeline'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-blue-600'
            }`}
          >
            🎯 Joining Pipeline
          </button>
          <button
            onClick={() => setActiveTab('probation')}
            className={`px-6 py-3 font-medium border-b-2 transition ${
              activeTab === 'probation'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-blue-600'
            }`}
          >
            ⏱️ Probation Tracking
          </button>
          <button
            onClick={() => setActiveTab('confirmation')}
            className={`px-6 py-3 font-medium border-b-2 transition ${
              activeTab === 'confirmation'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-blue-600'
            }`}
          >
            ✅ Confirmation
          </button>
        </div>

        {/* PIPELINE TAB */}
        {activeTab === 'pipeline' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-700">{stats.selected}</p>
                <p className="text-xs text-blue-600 mt-1">Selected</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-yellow-700">{stats.offer_sent}</p>
                <p className="text-xs text-yellow-600 mt-1">Offer Sent</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{stats.offer_accepted}</p>
                <p className="text-xs text-green-600 mt-1">Accepted</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-700">{documents.length}</p>
                <p className="text-xs text-purple-600 mt-1">Docs Generated</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-orange-700">{preTasks.length}</p>
                <p className="text-xs text-orange-600 mt-1">Pre-Tasks</p>
              </div>
              <div className="bg-pink-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-pink-700">{stats.employees_added}</p>
                <p className="text-xs text-pink-600 mt-1">Employees</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Candidate Pipeline */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg shadow-md p-4">
                  <h2 className="text-lg font-bold mb-4" style={{color: '#2b3380'}}>
                    <Users className="w-5 h-5 inline mr-2" />
                    Candidates in Pipeline
                  </h2>
                  
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {candidates.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-8">
                        No candidates in pipeline
                      </p>
                    ) : (
                      candidates.map((candidate) => (
                        <div
                          key={candidate.id}
                          onClick={() => setSelectedCandidate(candidate)}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition ${
                            selectedCandidate?.id === candidate.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-sm">
                                {candidate.first_name} {candidate.last_name}
                              </p>
                              <p className="text-xs text-gray-600">{candidate.designation}</p>
                              <span className={`inline-block px-2 py-1 rounded-full text-xs mt-2 ${getStageColor(candidate.stage)}`}>
                                {candidate.stage}
                              </span>
                            </div>
                            {selectedCandidate?.id === candidate.id && (
                              <CheckCircle className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Candidate Actions */}
              <div className="lg:col-span-2">
                {!selectedCandidate ? (
                  <div className="bg-white rounded-lg shadow-md p-12 text-center">
                    <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500">Select a candidate to view details</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Candidate Info Card */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                            {selectedCandidate.first_name} {selectedCandidate.last_name}
                          </h2>
                          <p className="text-gray-600">{selectedCandidate.designation}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStageColor(selectedCandidate.stage)}`}>
                          {selectedCandidate.stage}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                        <div>
                          <span className="text-gray-500">Email:</span>
                          <p className="font-medium">{selectedCandidate.email}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Phone:</span>
                          <p className="font-medium">{selectedCandidate.phone}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Location:</span>
                          <p className="font-medium">{selectedCandidate.current_location}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Expected CTC:</span>
                          <p className="font-medium">₹{selectedCandidate.expected_ctc?.toLocaleString() || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600">Journey Progress</span>
                          <span className="font-bold" style={{color: '#2b3380'}}>{getProgress().toFixed(0)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="h-3 rounded-full transition-all"
                            style={{
                              width: `${getProgress()}%`,
                              backgroundColor: '#2b3380'
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-bold mb-4" style={{color: '#2b3380'}}>Quick Actions</h3>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {/* Generate Offer Letter */}
                        {selectedCandidate.stage === 'Selected' && (
                          <>
                            <button
                              onClick={generateOfferLetter}
                              disabled={loading}
                              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-white font-medium"
                              style={{backgroundColor: loading ? '#9e9e9e' : '#2b3380'}}
                            >
                              <FileText className="w-5 h-5" />
                              Generate Offer Letter
                            </button>
                            
                            <button
                              onClick={markOfferAccepted}
                              className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-green-600 text-green-600 font-medium hover:bg-green-50"
                            >
                              <CheckCircle className="w-5 h-5" />
                              Mark Offer Accepted
                            </button>
                          </>
                        )}

                        {/* Generate Appointment Letter */}
                        {(selectedCandidate.stage === 'Offer Sent' || selectedCandidate.stage === 'Offer Accepted') && (
                          <button
                            onClick={generateAppointmentLetter}
                            disabled={loading}
                            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-white font-medium"
                            style={{backgroundColor: loading ? '#9e9e9e' : '#2b3380'}}
                          >
                            <Award className="w-5 h-5" />
                            Generate Appointment Letter
                          </button>
                        )}

                        {/* Convert to Employee */}
                        {selectedCandidate.stage === 'Offer Accepted' && (
                          <button
                            onClick={convertToEmployee}
                            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700"
                          >
                            <UserPlus className="w-5 h-5" />
                            Convert to Employee
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Pre-Joining Tasks */}
                    {selectedCandidate.stage === 'Offer Accepted' && (
                      <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-bold" style={{color: '#2b3380'}}>
                            <ClipboardList className="w-5 h-5 inline mr-2" />
                            Pre-Joining Tasks
                          </h3>
                          <button
                            onClick={() => navigate('/recruitment/pre-joining')}
                            className="text-sm text-blue-600 hover:underline"
                          >
                            Manage Tasks →
                          </button>
                        </div>
                        
                        {preTasks.length === 0 ? (
                          <p className="text-gray-500 text-sm text-center py-4">
                            No tasks assigned yet
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {preTasks.slice(0, 5).map((task) => (
                              <div key={task.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                  {task.status === 'Completed' ? (
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                                  )}
                                  <div>
                                    <p className="font-medium text-sm">{task.task_name}</p>
                                    <p className="text-xs text-gray-500">{task.task_type}</p>
                                  </div>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  task.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {task.status}
                                </span>
                              </div>
                            ))}
                            {preTasks.length > 5 && (
                              <p className="text-xs text-gray-500 text-center mt-2">
                                + {preTasks.length - 5} more tasks
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* PROBATION TAB */}
        {activeTab === 'probation' && (
          <>
            {/* Probation Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{probationEmployees.length}</p>
                <p className="text-xs text-gray-600 mt-1">On Probation</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-700">{probationEmployees.filter(e => e.probation_days_remaining <= 30).length}</p>
                <p className="text-xs text-gray-600 mt-1">Due This Month</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-orange-700">{probationEmployees.filter(e => !e.probation_review_date).length}</p>
                <p className="text-xs text-gray-600 mt-1">Pending Review</p>
              </div>
              <div className="bg-cyan-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-cyan-700">6 mo</p>
                <p className="text-xs text-gray-600 mt-1">Avg Duration</p>
              </div>
            </div>

            {/* Probation Employees Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{backgroundColor: '#2b3380'}}>
                    <tr>
                      <th className="px-6 py-3 text-left text-white">Employee</th>
                      <th className="px-6 py-3 text-left text-white">Designation</th>
                      <th className="px-6 py-3 text-left text-white">Department</th>
                      <th className="px-6 py-3 text-left text-white">Joining Date</th>
                      <th className="px-6 py-3 text-left text-white">Days Remaining</th>
                      <th className="px-6 py-3 text-left text-white">Status</th>
                      <th className="px-6 py-3 text-left text-white">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {probationEmployees.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <span className="text-4xl mb-2">✅</span>
                            <p>No employees on probation</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      probationEmployees.map((employee) => (
                        <tr key={employee.id} className="border-b hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{backgroundColor: '#2b3380'}}>
                                {employee.first_name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium">{employee.first_name} {employee.last_name}</p>
                                <p className="text-sm text-gray-500">{employee.employee_id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">{employee.designation || 'N/A'}</td>
                          <td className="px-6 py-4">{employee.department || 'N/A'}</td>
                          <td className="px-6 py-4">
                            {employee.date_of_joining ? new Date(employee.date_of_joining).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" style={{color: getProbationStatusColor(employee.probation_days_remaining)}} />
                              <span style={{color: getProbationStatusColor(employee.probation_days_remaining)}} className="font-medium">
                                {employee.probation_days_remaining} days
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {employee.probation_review_date ? (
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-600">
                                Reviewed
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-600">
                                Pending Review
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => {
                                setSelectedProbationEmployee(employee);
                                setReviewData({...reviewData, employee_id: employee.id});
                                setShowReviewForm(true);
                              }}
                              className="px-4 py-2 text-white rounded-lg text-sm"
                              style={{backgroundColor: '#2b3380'}}
                            >
                              Review
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* CONFIRMATION TAB */}
        {activeTab === 'confirmation' && (
          <>
            {/* Confirmation Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{confirmationEmployees.length}</p>
                <p className="text-xs text-gray-600 mt-1">Eligible</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-700">{confirmationEmployees.filter(e => e.probation_days_remaining <= 30).length}</p>
                <p className="text-xs text-gray-600 mt-1">This Month</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-700">0</p>
                <p className="text-xs text-gray-600 mt-1">Confirmed</p>
              </div>
              <div className="bg-cyan-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-cyan-700">95%</p>
                <p className="text-xs text-gray-600 mt-1">Success Rate</p>
              </div>
            </div>

            {/* Confirmation Employees Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold" style={{color: '#2b3380'}}>Employees Eligible for Confirmation</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{backgroundColor: '#2b3380'}}>
                    <tr>
                      <th className="px-6 py-3 text-left text-white">Employee</th>
                      <th className="px-6 py-3 text-left text-white">Designation</th>
                      <th className="px-6 py-3 text-left text-white">Department</th>
                      <th className="px-6 py-3 text-left text-white">Joining Date</th>
                      <th className="px-6 py-3 text-left text-white">Current Salary</th>
                      <th className="px-6 py-3 text-left text-white">Probation Status</th>
                      <th className="px-6 py-3 text-left text-white">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {confirmationEmployees.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <span className="text-4xl mb-2">🔍</span>
                            <p>No employees eligible for confirmation</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      confirmationEmployees.map((employee) => (
                        <tr key={employee.id} className="border-b hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{backgroundColor: '#2b3380'}}>
                                {employee.first_name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium">{employee.first_name} {employee.last_name}</p>
                                <p className="text-sm text-gray-500">{employee.employee_id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">{employee.designation || 'N/A'}</td>
                          <td className="px-6 py-4">{employee.department || 'N/A'}</td>
                          <td className="px-6 py-4">
                            {employee.date_of_joining ? new Date(employee.date_of_joining).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-medium">₹{employee.basic_salary?.toLocaleString() || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4">
                            {employee.probation_status === 'Confirm' ? (
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-600 flex items-center gap-1 w-fit">
                                <Award className="w-4 h-4" />
                                Recommended
                              </span>
                            ) : employee.probation_days_remaining <= 30 ? (
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-600">
                                {employee.probation_days_remaining} days left
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-600">
                                In Probation
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => {
                                setSelectedConfirmationEmployee(employee);
                                setConfirmationData({
                                  ...confirmationData,
                                  employee_id: employee.id,
                                  new_designation: employee.designation || ''
                                });
                                setShowConfirmationForm(true);
                              }}
                              className="px-4 py-2 text-white rounded-lg text-sm flex items-center gap-2"
                              style={{backgroundColor: '#2b3380'}}
                            >
                              <Award className="w-4 h-4" />
                              Confirm
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Probation Review Form Modal */}
        {showReviewForm && selectedProbationEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl my-8 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>
                Probation Review: {selectedProbationEmployee.first_name} {selectedProbationEmployee.last_name}
              </h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Employee ID</p>
                    <p className="font-medium">{selectedProbationEmployee.employee_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Designation</p>
                    <p className="font-medium">{selectedProbationEmployee.designation}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Joining Date</p>
                    <p className="font-medium">{new Date(selectedProbationEmployee.date_of_joining).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Probation Days Remaining</p>
                    <p className="font-medium" style={{color: getProbationStatusColor(selectedProbationEmployee.probation_days_remaining)}}>
                      {selectedProbationEmployee.probation_days_remaining} days
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleProbationReviewSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Review Date</label>
                  <input
                    type="date"
                    value={reviewData.review_date}
                    onChange={(e) => setReviewData({...reviewData, review_date: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <RatingStars
                  label="Performance Rating *"
                  value={reviewData.performance_rating}
                  onChange={(val) => setReviewData({...reviewData, performance_rating: val})}
                />

                <RatingStars
                  label="Attendance Rating *"
                  value={reviewData.attendance_rating}
                  onChange={(val) => setReviewData({...reviewData, attendance_rating: val})}
                />

                <RatingStars
                  label="Behavior & Cultural Fit *"
                  value={reviewData.behavior_rating}
                  onChange={(val) => setReviewData({...reviewData, behavior_rating: val})}
                />

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Manager Feedback *</label>
                  <textarea
                    value={reviewData.manager_feedback}
                    onChange={(e) => setReviewData({...reviewData, manager_feedback: e.target.value})}
                    rows="4"
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Detailed feedback on performance, strengths, areas of improvement..."
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Recommendation *</label>
                  <div className="grid grid-cols-3 gap-4">
                    {['Confirm', 'Extend Probation', 'Terminate'].map(rec => (
                      <button
                        key={rec}
                        type="button"
                        onClick={() => setReviewData({...reviewData, recommendation: rec})}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          reviewData.recommendation === rec
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-blue-300'
                        }`}
                      >
                        {rec === 'Confirm' && <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />}
                        {rec === 'Extend Probation' && <Clock className="w-8 h-8 mx-auto mb-2 text-orange-500" />}
                        {rec === 'Terminate' && <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />}
                        <p className="text-sm font-medium">{rec}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button type="submit" className="px-6 py-2 text-white rounded-lg" style={{backgroundColor: '#2b3380'}}>
                    Submit Review
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowReviewForm(false); setSelectedProbationEmployee(null); resetReviewForm(); }}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Confirmation Form Modal */}
        {showConfirmationForm && selectedConfirmationEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-8 h-8" style={{color: '#2b3380'}} />
                <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                  Issue Confirmation Letter
                </h2>
              </div>
              
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Employee</p>
                    <p className="font-medium">{selectedConfirmationEmployee.first_name} {selectedConfirmationEmployee.last_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Employee ID</p>
                    <p className="font-medium">{selectedConfirmationEmployee.employee_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Current Designation</p>
                    <p className="font-medium">{selectedConfirmationEmployee.designation}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Current Salary</p>
                    <p className="font-medium">₹{selectedConfirmationEmployee.basic_salary?.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleConfirmationSubmit}>
                <div className="grid grid-cols-1 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Confirmation Date *</label>
                    <input
                      type="date"
                      value={confirmationData.confirmation_date}
                      onChange={(e) => setConfirmationData({...confirmationData, confirmation_date: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      New Designation (Optional - if promoted)
                    </label>
                    <input
                      type="text"
                      value={confirmationData.new_designation}
                      onChange={(e) => setConfirmationData({...confirmationData, new_designation: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Leave blank if no change"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Revised Basic Salary (Optional - ₹)
                    </label>
                    <input
                      type="number"
                      value={confirmationData.salary_revision}
                      onChange={(e) => setConfirmationData({...confirmationData, salary_revision: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Leave 0 if no revision"
                    />
                    {confirmationData.salary_revision > 0 && (
                      <p className="text-sm text-green-600 mt-1">
                        Increment: ₹{(confirmationData.salary_revision - (selectedConfirmationEmployee.basic_salary || 0)).toLocaleString()}
                        ({(((confirmationData.salary_revision - (selectedConfirmationEmployee.basic_salary || 0)) / (selectedConfirmationEmployee.basic_salary || 1)) * 100).toFixed(1)}%)
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Additional Benefits (Optional)</label>
                    <textarea
                      value={confirmationData.additional_benefits.join(', ')}
                      onChange={(e) => setConfirmationData({
                        ...confirmationData,
                        additional_benefits: e.target.value.split(',').map(b => b.trim()).filter(b => b)
                      })}
                      rows="3"
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., Health Insurance, Performance Bonus, Stock Options (comma-separated)"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-bold mb-2" style={{color: '#2b3380'}}>Confirmation Summary</h4>
                  <ul className="text-sm space-y-1">
                    <li>✅ Employee probation period completed successfully</li>
                    <li>✅ Performance review: {selectedConfirmationEmployee.probation_status || 'Recommended'}</li>
                    {confirmationData.new_designation && confirmationData.new_designation !== selectedConfirmationEmployee.designation && (
                      <li>👑 Promoted to: {confirmationData.new_designation}</li>
                    )}
                    {confirmationData.salary_revision > 0 && (
                      <li>💰 Salary revised to: ₹{confirmationData.salary_revision.toLocaleString()}</li>
                    )}
                  </ul>
                </div>

                <div className="flex gap-2">
                  <button type="submit" className="px-6 py-2 text-white rounded-lg flex items-center gap-2" style={{backgroundColor: '#2b3380'}}>
                    <Award className="w-5 h-5" />
                    Issue Confirmation Letter
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowConfirmationForm(false); setSelectedConfirmationEmployee(null); resetConfirmationForm(); }}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default UnifiedJoiningFlow;
